import React, { FunctionComponent, useContext } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormFields,
    FormTextField,
    Stack,
    getById,
    FormAdditionalValidation
} from "sprint-common-web";

import { MuxerType } from "../../../../../common/types/MuxerType";
import { MuxSpecificationListingContext } from "../../../context/MuxSpecificationListingContext";
import { GpacMuxDetails, MuxDetails } from "../../MuxSpecificationListingItem";
import { useGpacCommandValidation } from "../useGpacCommandValidation";
import { useEditGpacMuxSpecificationForm } from "./useEditGpacMuxSpecificationForm";

export interface EditFramerateProfileFormProps {
    itemId: string;
    onCancel(): void;
    onSubmit(): void;
}

export interface EditGpacMuxSpecificationFormInputs {
    name: string;
    command: string;
    soundtracksCommand: string;
    subtitlesCommand: string;
}

export const EditGpacMuxSpecificationForm: FunctionComponent<EditFramerateProfileFormProps> = (
    props: EditFramerateProfileFormProps
) => {
    const { itemId, onCancel, onSubmit } = props;
    const { items } = useContext(MuxSpecificationListingContext);
    const { editGpacMuxSpecification } = useEditGpacMuxSpecificationForm();
    const {
        validateCommand,
        validateCommandsCorrelation,
        validateSoundtracksCommand,
        validateSubtitlesCommand
    } = useGpacCommandValidation();

    const item = getById(items, itemId);
    const gpacMuxDetails = item.details;
    ensureGpacMuxDetails(gpacMuxDetails);

    const { errors, formState, control, reset, handleSubmit, getValues } = useForm<EditGpacMuxSpecificationFormInputs>({
        defaultValues: {
            name: item.name,
            command: gpacMuxDetails.command,
            soundtracksCommand: gpacMuxDetails.soundtracksCommand,
            subtitlesCommand: gpacMuxDetails.subtitlesCommand
        },
        criteriaMode: "all"
    });

    const handleEditGpacMuxSpecification = async (inputs: EditGpacMuxSpecificationFormInputs) => {
        await editGpacMuxSpecification(props.itemId, inputs);
        onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleEditGpacMuxSpecification)}>
                <Stack verticalAlign="space-between">
                    <FormFields>
                        <FormAdditionalValidation
                            control={control}
                            name="additionalValidation"
                            errors={errors}
                            rules={{ validate: () => validateCommandsCorrelation(getValues()) }}
                        />
                        <FormTextField
                            label="Name"
                            control={control}
                            name="name"
                            errors={errors}
                            rules={{
                                required: "Name is required field"
                            }}
                        />
                        <FormTextField
                            label="Command"
                            control={control}
                            name="command"
                            errors={errors}
                            rules={{
                                required: "Command is required field",
                                validate: validateCommand
                            }}
                            tooltipContent={`Available tokens: {soundtracksCommand} {subtitlesCommand}`}
                        />
                        <FormTextField
                            label="Soundtracks command"
                            control={control}
                            name="soundtracksCommand"
                            errors={errors}
                            rules={{
                                required: "Soundtracks command is required field",
                                validate: validateSoundtracksCommand
                            }}
                            tooltipContent={`Available tokens: {soundtracksFilePath} {language.iso639} {language.laRg} {languageSpec}`}
                        />
                        <FormTextField
                            label="Subtitles command"
                            control={control}
                            name="subtitlesCommand"
                            errors={errors}
                            rules={{
                                validate: validateSubtitlesCommand
                            }}
                            tooltipContent={`Available tokens: {subtitlesFilePath} {language.iso639} {language.laRg} {languageSpec}`}
                        />
                    </FormFields>
                    <FormActions
                        isLoading={formState.isSubmitting || formState.isValidating}
                        onCancel={handleCancelButtonClick}
                    />
                </Stack>
            </Form>
        </Stack>
    );
};

const ensureGpacMuxDetails: (value: MuxDetails) => asserts value is GpacMuxDetails = (value) => {
    if (value.type !== MuxerType.GPAC) {
        throw Error("Mux details should be of type 'gpac'");
    }
};
